import dayjs, {Dayjs} from 'dayjs';
import React, {useEffect, useState} from 'react';

import localeData from 'dayjs/plugin/localeData';
import weekday from 'dayjs/plugin/weekday';
import 'dayjs/locale/de-at';

dayjs.extend(localeData);
dayjs.extend(weekday);

dayjs.locale('de-at');

import {useTheme} from '..';

enum DayStatus {
    'Default',
    'Start',
    'End',
    'Single',
    'Bridge',
}

interface Day {
    date: Dayjs | null;
    status: DayStatus;
}

function CalendardHeaderDay({day}: { day: string }) {
    return (
        <th className="flex grow justify-center">
            <div
                className="flex justify-center items-center px-2 py-2"
                style={{width: 32, height: 32}}
            >
        <span className="text-base font-medium text-center text-gray-800">
          {day}
        </span>
            </div>
        </th>
    );
}

function CalendarMonth({
                           date,
                           selectedDates,
                           onClick,
                       }: {
    date: Dayjs;
    selectedDates: Date[][];
    onClick?: (day: Dayjs) => void;
}) {
    const [weeks, setWeeks] = useState(generateCalendar(dayjs(), []));
    useEffect(() => {
        if (date == null) return;
        const calendar = generateCalendar(date, selectedDates);
        setWeeks(calendar);
    }, [date, selectedDates]);

    return (
        <>
            {weeks.map((w, i) => {
                return <CalendarWeek key={i} onClick={onClick} days={w}/>;
            })}
        </>
    );
}

function CalendarWeek({
                          days,
                          onClick,
                      }: {
    days: Day[];
    onClick?: (day: Dayjs) => void;
}) {
    return (
        <tr className="flex">
            {days.map((d, i) => {
                return <CalendardDay key={i} day={d} onClick={onClick}/>;
            })}
        </tr>
    );
}

function CalendardDay({
                          day,
                          onClick,
                      }: {
    day: Day;
    onClick?: (day: Dayjs) => void;
}) {
    const {primaryColor, primaryLight} = useTheme();
    const hasDay = day !== null && day.date !== null; // Check if the day is not a placeholder
    let style: React.CSSProperties = {};
    const today = dayjs();
    const isToday = hasDay && day?.date && today.isSame(day?.date, 'day');
    const isBeforeToday = hasDay && day?.date && day?.date.isBefore(today, 'day');
    const isClickable = hasDay && onClick !== undefined;

    if (hasDay) {
        // Check if day.date is not null
        switch (day.status) {
            case DayStatus.Single:
            case DayStatus.Start:
            case DayStatus.End:
                style = {
                    backgroundColor: primaryColor,
                    color: 'white',
                };
                break;
            case DayStatus.Bridge:
                style = {
                    backgroundColor: primaryLight,
                };
                break;
            case DayStatus.Default:
                style = {};
        }
    }

    return (
        <td className="flex grow justify-center">
            <div
                onClick={
                    !isClickable
                        ? () => {
                        }
                        : () => {
                            if (day.date) {
                                // This ensures day.date is non-null before calling onClick
                                onClick(day.date);
                            }
                        }
                }
                className={`flex justify-center items-center px-2 py-2
            ${isBeforeToday ? 'opacity-50' : ''}
            ${isToday ? 'outline outline-2' : ''}
            ${isClickable ? 'cursor-pointer' : ''}`}
                style={{
                    ...style,
                    outlineColor: isToday ? primaryColor : style.outlineColor,
                    backgroundColor: isToday ? 'white' : style.backgroundColor,
                    color: isToday ? primaryColor : style.color,
                    width: 32,
                    height: 32,
                }}
            >
                {hasDay && day.date ? day.date.date().toString() : ''}
            </div>
        </td>
    );
}

const generateCalendar = (date: Dayjs, selectedDates: Date[][]) => {
    const startOfMonth = date.startOf('month');
    const firstDayOfWeek = dayjs().localeData().firstDayOfWeek(); // Get locale-specific first day of the week
    const daysInMonth = date.daysInMonth();

    const selectionMap = new Map<number, DayStatus>();

    // Map selected dates to their respective statuses
    for (const range of selectedDates) {
        if (range.length === 0) continue;

        const start = dayjs(range[0]);
        const end = range.length === 2 ? dayjs(range[1]) : null;

        if (!start.isValid() || (end && !end.isValid())) {
            continue;
        }

        if (
            range.length === 1 ||
            (end &&
                start.isSame(end, 'day') &&
                start.isSame(end, 'month') &&
                start.isSame(end, 'year'))
        ) {
            if (start.isSame(date, 'month') && start.isSame(date, 'year')) {
                selectionMap.set(start.date(), DayStatus.Single);
            }
        } else if (range.length === 2 && end) {
            const diff = end.diff(start, 'day');
            for (let i = 0; i <= diff; i++) {
                const d = start.add(i, 'day');
                if (d.isSame(date, 'month') && d.isSame(date, 'year')) {
                    if (i === 0) {
                        selectionMap.set(d.date(), DayStatus.Start);
                    } else if (i === diff) {
                        selectionMap.set(d.date(), DayStatus.End);
                    } else {
                        selectionMap.set(d.date(), DayStatus.Bridge);
                    }
                }
            }
        } else if (range.length === 1) {
            // Handle open-ended range
            if (start.isSame(date, 'month') && start.isSame(date, 'year')) {
                selectionMap.set(start.date(), DayStatus.Start);
                for (let i = start.date() + 1; i <= daysInMonth; i++) {
                    selectionMap.set(i, DayStatus.Bridge);
                }
            }
        }
    }

    const dayOfWeek = (startOfMonth.day() - firstDayOfWeek + 7) % 7; // Adjust for locale-specific first day of the week

    // Create array of days in the month, adjusting for the locale-specific first day of the week
    const daysArray: Day[] = new Array(dayOfWeek).fill(null).concat(
        [...Array(daysInMonth).keys()].map((i) => {
            const status = selectionMap.get(i + 1) ?? DayStatus.Default;
            return {
                date: date.date(i + 1),
                status: status,
            };
        })
    );

    const placeholderDay: Day = {date: null, status: DayStatus.Default};

    const weeks: Day[][] = [];
    for (let i = 0; i < daysArray.length; i += 7) {
        const week = daysArray.slice(i, i + 7);
        while (week.length < 7) {
            week.push(placeholderDay);
        }
        weeks.push(week);
    }

    return weeks;
};

interface CalendarProps extends React.HTMLAttributes<HTMLDivElement> {
    selectedDates: Date[][];
    onClickDate?: (date: Date) => void;
    initialDate?: Date;
}

export function Calendar(props: CalendarProps) {
    const getInitialDate = () => {
        // If initialDate prop is provided, use it
        if (props.initialDate) {
            return dayjs(props.initialDate);
        }
        // If there are selected dates, use the first one
        if (props.selectedDates.length > 0 && props.selectedDates[0].length > 0) {
            return dayjs(props.selectedDates[0][0]);
        }
        // Default to today
        return dayjs();
    };

    const [checkedDate, setCheckedDate] = useState<Dayjs>(getInitialDate());

    const {primaryColor} = useTheme();

    const {selectedDates, onClickDate, ...rest} = props;

    // Update checkedDate when selectedDates change and there are selected dates
    useEffect(() => {
        if (selectedDates.length > 0 && selectedDates[0].length > 0) {
            const firstSelectedDate = dayjs(selectedDates[0][0]);
            // Only update if the selected date is in a different month than currently displayed
            if (!firstSelectedDate.isSame(checkedDate, 'month')) {
                setCheckedDate(firstSelectedDate);
            }
        }
    }, [selectedDates]);

    const selectedDateInFutureMonth = selectedDates.some((range) => {
        return range.some((d) => {
            return dayjs(d).isAfter(checkedDate.endOf('month'));
        });
    });

    const selectedDateInPastMonth = selectedDates.some((range) => {
        return range.some((d) => {
            return dayjs(d).isBefore(checkedDate.startOf('month'));
        });
    });

    return (
        <div {...rest}>
            <div className="px-4 flex items-center justify-between">
                <button
                    aria-label="calendar backward"
                    className={
                        selectedDateInPastMonth
                            ? ''
                            : 'focus:text-gray-400 hover:text-gray-400 text-gray-800'
                    }
                    style={selectedDateInPastMonth ? {color: primaryColor} : {}}
                    onClick={() => setCheckedDate(checkedDate.subtract(1, 'month'))}
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="icon icon-tabler icon-tabler-chevron-left"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        strokeWidth="1.5"
                        stroke="currentColor"
                        fill="none"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    >
                        <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                        <polyline points="15 6 9 12 15 18"/>
                    </svg>
                </button>

                <span
                    tabIndex={0}
                    className="focus:outline-none  text-base font-bold text-gray-800"
                >{checkedDate.format('MMMM YYYY')}</span>
                <button
                    aria-label="calendar forward"
                    className={
                        selectedDateInFutureMonth
                            ? 'ml-3'
                            : 'focus:text-gray-400 hover:text-gray-400 ml-3 text-gray-800'
                    }
                    style={selectedDateInFutureMonth ? {color: primaryColor} : {}}
                    onClick={() => setCheckedDate(checkedDate.add(1, 'month'))}
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="icon icon-tabler  icon-tabler-chevron-right"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        strokeWidth="1.5"
                        stroke="currentColor"
                        fill="none"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    >
                        <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                        <polyline points="9 6 15 12 9 18"/>
                    </svg>
                </button>
            </div>
            <div className="flex items-center justify-between py-4 overflow-x-auto">
                <table className="w-full">
                    <thead>
                    <tr className="flex">
                        {dayjs.weekdaysMin(true).map((d, i) => (
                            <CalendardHeaderDay key={i} day={d}/>
                        ))}
                    </tr>
                    </thead>
                    <tbody className="flex flex-col">
                    <CalendarMonth
                        selectedDates={selectedDates}
                        onClick={onClickDate ? (d) => onClickDate(d.toDate()) : undefined}
                        date={checkedDate}
                    />
                    </tbody>
                </table>
            </div>
        </div>
    );
}
